import { computed, Injectable, signal } from '@angular/core';

/**
 * Bridge between the onboarding shell layout (top bar) and whichever page is rendered inside it.
 *
 * The Figma onboarding chrome puts the page label and the "Voortgang opslaan" action in the top
 * bar, but both belong to the onboarding page: only that page knows the onboarding-form name and
 * how to persist the partially filled entry. The page registers them on init and releases them on
 * destroy; the layout renders whatever is registered and nothing when the slot is empty.
 *
 * Registration is OWNED: `claim()` hands out a token and `release(token)` only clears when that
 * token is still the current owner. Angular can briefly keep two instances of the same routed
 * component alive (a layout switch re-activates the route before the old instance is torn down),
 * and without the token the dying instance would unregister the live one's title and save action.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingShellService {
  /** Label next to the logo in the top bar. Null renders the logo on its own. */
  readonly title = signal<string | null>(null);

  /** Handler behind the "Voortgang opslaan" action; null hides the action entirely. */
  readonly #saveProgress = signal<(() => void) | null>(null);

  /** True while a save-progress handler is registered by the hosted page. */
  readonly canSaveProgress = computed(() => this.#saveProgress() !== null);

  /** Set by the page while a save is in flight so the top bar can show progress. */
  readonly saving = signal(false);

  /** Identifies the page instance currently allowed to write to this service. */
  #owner: symbol | null = null;

  /** Take ownership of the shell slots. Keep the returned token for {@link release}. */
  claim(): symbol {
    const token = Symbol('onboarding-shell-owner');
    this.#owner = token;
    return token;
  }

  /** Register (or with `null` unregister) the save-progress handler. Ignored for a stale owner. */
  registerSaveProgress(token: symbol, handler: (() => void) | null): void {
    if (this.#owner !== token) {
      return;
    }
    this.#saveProgress.set(handler);
  }

  /** Set the top-bar label. Ignored for a stale owner. */
  setTitle(token: symbol, title: string | null): void {
    if (this.#owner !== token) {
      return;
    }
    this.title.set(title);
  }

  /** Run the registered handler. No-op when the hosted page did not register one. */
  saveProgress(): void {
    this.#saveProgress()?.();
  }

  /**
   * Clear everything the owning page registered — call from its `ngOnDestroy`. A page that no
   * longer owns the slots (because a newer instance claimed them) leaves them untouched.
   */
  release(token: symbol): void {
    if (this.#owner !== token) {
      return;
    }
    this.#owner = null;
    this.title.set(null);
    this.saving.set(false);
    this.#saveProgress.set(null);
  }
}
